import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getPublicDealsPath, getPublicDealsRetailer, type PublicDealsRetailer } from "../config/publicDeals";
import useSeo from "../hooks/useSeo";
import usePageBackground from "../hooks/usePageBackground";
import { useTheme } from "../context/ThemeContext";
import NotFoundPage from "./NotFoundPage";
import SiteFooter from "../components/home-feed/SiteFooter";
import { browsePublicProducts, getPublicProduct } from "../services/ProductsService";
import type { ProductListItem } from "../types/Product";
import { track } from "../utils/analytics";
import { buildGetStartedUrl, buildGuestDealAuthReturnTo } from "../utils/authReturn";
import { useAuthModal } from "../hooks/useAuthModal";
import { BASIC_INTRO_LABEL } from "../utils/basicPlanOffer";
import { getProductImageUrl } from "../utils/imageUtils";
import { openGuestRetailerDeal } from "../components/guest-deals/GuestDealsUi";
import { getGuestDealDiscount, getGuestDealPrice } from "../components/guest-deals/GuestDealsUi.utils";
import { categoryTitle } from "../components/home-feed/DealRow";
import type { DealGateAction } from "../components/home-feed/DealDetailModal";
import { HF, LockIcon, cleanName, retailerLabel } from "../components/home-feed/homeFeedUi";
import { FreeMemberHeaderActions, freeUpgradeUrl, trackFreeUpgrade, useHomeFeedViewer } from "../components/home-feed/viewer";

const SOURCE = "product_page";
const INK = "#170B26";
/** Theme-aware colours (CSS variables in src/index.css); the footer toggle switches them. */
const PAGE_BG = "var(--hf-deal-bg)";
const TEXT = HF.ink;
const CARD = "rounded-[14px] border bg-[var(--hf-surface)] shadow-[0_1px_2px_var(--hf-shadow-sm)]";
const CARD_BORDER = { borderColor: "var(--hf-deal-border)" };
const LINE = "var(--hf-deal-line)";
const CONTROL_BORDER = "var(--hf-deal-control)";
const LABEL = HF.text3;
const MUTED = HF.body;
const GREEN = HF.green;
const GREEN_FILL = "#0B6B45";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const shortDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dayTime = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Path of a deal's own page. The product key is the opaque, expiring id the browse feed hands out. */
export function homeDealPath(retailer: PublicDealsRetailer, productKey: string): string {
  return `/${retailer.slug}/item/${encodeURIComponent(productKey)}`;
}

function formatDate(ms: number | undefined, withTime = false): string | null {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return null;
  return (withTime ? dayTime : day).format(new Date(ms));
}

function sameDeal(a: ProductListItem, b: ProductListItem): boolean {
  return a.name === b.name && a.image === b.image;
}

export default function HomeDealPage() {
  const { isDark } = useTheme();
  usePageBackground(PAGE_BG, isDark ? "#0B0B0D" : INK);
  const { freeMember } = useHomeFeedViewer();
  // Sign in / sign up opens over the deal instead of leaving for /get-started.
  const auth = useAuthModal();
  const { retailerSlug = "", productKey = "" } = useParams<{ retailerSlug: string; productKey: string }>();
  const retailer = getPublicDealsRetailer(retailerSlug);
  const location = useLocation();
  const navigate = useNavigate();
  const passed = (location.state as { product?: ProductListItem } | null)?.product;
  const passedProduct = passed && passed.productKey === productKey ? passed : null;

  const fetched = useQuery({
    queryKey: ["public-product", productKey],
    enabled: Boolean(retailer && productKey && !passedProduct),
    retry: 1,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ProductListItem> => {
      const p = await getPublicProduct(productKey);
      return {
        productKey: p.productKey || productKey,
        upc: "",
        sku: "",
        name: p.name,
        msrp: p.msrp,
        onlinePrice: p.onlinePrice,
        image: p.image,
        storeId: "online",
        storePrice: p.currentPrice > 0 ? [p.currentPrice] : [],
        backroom: [],
        salesFloor: [],
        aisle: "",
        firstInserted: 0,
        lastUpdated: p.lastUpdated,
        lastUpdates: [],
        category: p.category,
        adjustedCategory: p.adjustedCategory,
        "storeId-upc": "",
        inStock: p.inStock,
        discountPercentage: p.discountPercentage,
      };
    },
  });
  const product = passedProduct || fetched.data || null;
  const categoryId = product?.adjustedCategory || "";

  const similarQuery = useQuery({
    queryKey: ["public-products-similar", retailer?.apiRetailer, categoryId],
    enabled: Boolean(retailer && product),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      browsePublicProducts({
        retailer: retailer!.apiRetailer,
        storeId: retailer!.storeId,
        searchTokens: [],
        categories: categoryId ? [categoryId] : [],
        status: "all",
        sortBy: "discountPercentage",
        sortDirection: "desc",
        limit: 12,
      }),
  });
  const similar = (similarQuery.data?.products || []).filter((item) => !product || !sameDeal(item, product)).slice(0, 5);

  const label = retailer ? retailerLabel(retailer) : "";
  const price = product ? getGuestDealPrice(product) : 0;
  const msrp = product ? Number(product.msrp) || 0 : 0;
  const discount = product ? getGuestDealDiscount(product) : 0;
  const savings = msrp > price && price > 0 ? msrp - price : 0;
  const name = product ? cleanName(product.name) : "";
  const category = product?.category || (categoryId ? categoryTitle(categoryId) : "");
  const firstTracked = formatDate(product?.firstInserted);
  const checked = formatDate(product?.lastUpdated, true);
  const facts: { label: string; value: string; color?: string }[] = product
    ? [
        { label: "Deal price", value: price > 0 ? money.format(price) : "—" },
        { label: "List price", value: msrp > 0 ? money.format(msrp) : "—" },
        { label: "You save", value: savings > 0 ? `${money.format(savings)} (${discount}%)` : "—", color: savings > 0 ? GREEN : undefined },
        { label: "Sold by", value: retailer ? retailerLabel(retailer) : "" },
        ...(firstTracked ? [{ label: "First tracked", value: firstTracked }] : []),
        ...(checked ? [{ label: "Price checked", value: checked }] : []),
      ]
    : [];

  useSeo(
    retailer && product
      ? {
          title: `${name}${price > 0 ? ` — ${money.format(price)}` : ""} at ${label} | DealHawk`,
          description: `${name}${price > 0 ? ` is ${money.format(price)} at ${label}` : ` at ${label}`}${discount > 0 ? `, ${discount}% off list` : ""}.`,
          path: location.pathname,
          image: product.image || undefined,
          // Product keys are sealed per request and expire, so these URLs must not be crawled.
          robots: "noindex, nofollow",
        }
      : null,
  );

  const [opening, setOpening] = useState(false);
  const [outboundFailed, setOutboundFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  if (!retailer) return <NotFoundPage />;

  const dealsPath = getPublicDealsPath(retailer.slug);
  const signupUrl = buildGetStartedUrl("", "product_page_signup");
  const loginUrl = buildGetStartedUrl("", "product_page_login");
  const instoreUrl = freeMember
    ? freeUpgradeUrl("product_page_instore")
    : buildGetStartedUrl(buildGuestDealAuthReturnTo("/deals", "local"), "product_page_instore");

  const gate = (action: DealGateAction) => {
    if (freeMember) {
      trackFreeUpgrade(`product_page_${action}`, retailer.slug);
      navigate(freeUpgradeUrl(`product_page_${action}`));
      return;
    }
    track("guest_signup_prompt_viewed", { surface: "web", retailer: retailer.slug, trigger: `page_${action}`, source: SOURCE });
    auth.openAuth(buildGuestDealAuthReturnTo(dealsPath, action, productKey), `product_page_${action}`);
  };

  const openRetailer = async () => {
    if (!product || opening) return;
    setOpening(true);
    setOutboundFailed(false);
    const opened = await openGuestRetailerDeal(product, (url) => {
      let host = "retailer_link";
      try { host = new URL(url).hostname; } catch { /* generic */ }
      track("guest_outbound_clicked", { surface: "web", retailer: retailer.slug, trigger: "product_page", source: host });
    });
    setOpening(false);
    setOutboundFailed(!opened);
  };

  const openSimilar = (item: ProductListItem) => {
    if (!item.productKey) return;
    track("guest_deal_opened", { surface: "web", retailer: retailer.slug, trigger: "similar_deal", source: SOURCE });
    navigate(homeDealPath(retailer, item.productKey), { state: { product: item } });
    window.scrollTo({ top: 0 });
  };

  const image = product ? getProductImageUrl(product.image, "600") : "";

  return (
    <div className="min-h-full text-[14px]" style={{ background: PAGE_BG, color: TEXT, fontFamily: HF.font, colorScheme: "var(--hf-scheme)" }} onClickCapture={auth.interceptAuthLinks}>
      <header className="border-b text-white" style={{ background: HF.header, borderColor: "var(--hf-header-border)" }}>
        <div className="mx-auto flex h-14 max-w-[1168px] items-center gap-3 px-4 sm:px-6">
          <Link to="/" aria-label="DealHawk home" className="flex flex-shrink-0 items-center gap-2 text-white no-underline">
            <img src="/hawk_icon_white.svg" alt="" width={26} height={26} className="h-[26px] w-[26px] object-contain" />
            <span className="text-[17px] font-extrabold tracking-[-0.3px]">DealHawk</span>
          </Link>
          <span className="flex-1" />
          {freeMember ? (
            <FreeMemberHeaderActions source="product_page" retailer={retailer.slug} />
          ) : (
            <>
              <Link to={loginUrl} className="whitespace-nowrap px-1.5 py-2 text-[13.5px] font-semibold text-[#E9E0F4] no-underline hover:text-white">
                Log in
              </Link>
              <Link
                to={signupUrl}
                onClick={() => track("guest_signup_started", { surface: "web", retailer: retailer.slug, trigger: "header", source: SOURCE })}
                className="flex h-9 items-center whitespace-nowrap rounded-[9px] bg-white px-3.5 text-[13.5px] font-bold no-underline hover:bg-[#F4F0FA]"
                style={{ color: INK }}
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-[1168px] px-3 pb-28 pt-3 sm:px-6 sm:pb-12 sm:pt-4">
        <nav aria-label="Breadcrumb" className="mb-3 flex items-center gap-1.5 px-1 text-[13px]" style={{ color: MUTED }}>
          <Link to="/" className="no-underline hover:underline" style={{ color: MUTED }}>Deals</Link>
          <span aria-hidden="true">›</span>
          <Link to={`/${retailer.slug}`} className="no-underline hover:underline" style={{ color: MUTED }}>{label}</Link>
          {category && categoryId && (
            <>
              <span aria-hidden="true">›</span>
              <Link to={`/${retailer.slug}/${encodeURIComponent(categoryId)}`} className="no-underline hover:underline" style={{ color: MUTED }}>{category}</Link>
            </>
          )}
        </nav>

        {!product ? (
          fetched.isError ? (
            <div className={`${CARD} mx-auto max-w-md px-6 py-10 text-center`} style={CARD_BORDER} role="alert">
              <p className="text-base font-bold">This deal link has expired</p>
              <p className="mt-1 text-sm" style={{ color: MUTED }}>Prices change fast. See what's on sale at {label} right now.</p>
              <Link to={`/${retailer.slug}`} className="mt-4 inline-flex h-10 items-center rounded-[10px] px-4 text-sm font-bold no-underline" style={{ background: HF.inkBtn, color: HF.onInkBtn }}>
                Browse {label} deals
              </Link>
            </div>
          ) : (
            <div className={`${CARD} h-[300px] animate-pulse`} style={CARD_BORDER} role="status" aria-label="Loading deal" />
          )
        ) : (
          <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:grid-rows-[auto_auto_1fr] lg:[grid-template-areas:'main_buy'_'main_side'_'similar_side']">
              <section className={`${CARD} flex min-w-0 flex-col gap-4 p-3.5 sm:flex-row sm:gap-[22px] sm:p-5 lg:[grid-area:main]`} style={CARD_BORDER} data-testid="product-page-main">
                <div className="flex h-[220px] flex-shrink-0 items-center justify-center rounded-[10px] sm:h-[260px] sm:w-[260px]" style={{ background: HF.tile }}>
                  {image && !imageFailed ? (
                    <img src={image} alt={name} onError={() => setImageFailed(true)} className="h-[86%] w-[86%] object-contain mix-blend-multiply" />
                  ) : null}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    <span className="flex h-[22px] items-center rounded-md px-2 text-[12px] font-bold" style={{ background: "var(--hf-blue-soft)", color: "var(--hf-blue-text)" }}>{label}</span>
                    {category && <span className="flex h-[22px] items-center rounded-md px-2 text-[12px] font-semibold" style={{ background: HF.bg, color: HF.text2 }}>{category}</span>}
                    {product.inStock && <span className="flex h-[22px] items-center rounded-md px-2 text-[12px] font-bold" style={{ background: "var(--hf-green-soft)", color: GREEN }}>In stock online</span>}
                  </div>
                  <h1 className="m-0 text-[17px] font-bold leading-[23px] sm:text-[21px] sm:leading-[27px] sm:tracking-[-0.2px]">{name}</h1>
                  <dl className="m-0 mt-1 grid grid-cols-2 overflow-hidden rounded-[10px] border" style={{ borderColor: LINE }}>
                    {facts.map((fact, index) => (
                      <Fact key={fact.label} label={fact.label} value={fact.value} color={fact.color} right={index % 2 === 1} last={index >= facts.length - (facts.length % 2 === 0 ? 2 : 1)} />
                    ))}
                  </dl>
                </div>
              </section>

              <section className={`${CARD} flex flex-col gap-3 p-4 lg:[grid-area:buy]`} style={CARD_BORDER} data-testid="product-page-buy">
                <div className="flex items-baseline gap-2">
                  <span className="text-[26px] font-extrabold tabular-nums tracking-[-0.5px] sm:text-[30px] sm:tracking-[-0.6px]">{price > 0 ? money.format(price) : "See price"}</span>
                  {msrp > price && price > 0 && <span className="text-[14px] tabular-nums line-through" style={{ color: HF.muted }}>{money.format(msrp)}</span>}
                  {discount > 0 && (
                    <span className="ml-auto flex h-6 items-center self-center rounded-md px-2 text-[12.5px] font-bold text-white" style={{ background: GREEN_FILL }}>−{discount}%</span>
                  )}
                </div>
                <span className="-mt-1.5 text-[13px]" style={{ color: MUTED }}>
                  Online price at {label}{checked ? ` · checked ${shortDay.format(new Date(product.lastUpdated as number))}` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => void openRetailer()}
                  disabled={opening}
                  className="flex h-11 items-center justify-center gap-2 rounded-[10px] text-[14px] font-bold transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-70"
                  style={{ background: HF.inkBtn, color: HF.onInkBtn }}
                >
                  {opening ? "Opening…" : `View on ${label}`}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
                </button>
                {outboundFailed && <p className="m-0 text-center text-[12.5px] font-semibold" style={{ color: HF.red }}>This retailer link is temporarily unavailable.</p>}
                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton onClick={() => gate("alert")} icon={<BellIcon />}>Price alert</SecondaryButton>
                  <SecondaryButton onClick={() => gate("save")} icon={<HeartOutline />}>Save</SecondaryButton>
                </div>
              </section>

            <aside className="flex flex-col gap-3 sm:gap-4 lg:[grid-area:side]">
              <section className={`${CARD} overflow-hidden`} style={CARD_BORDER}>
                <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: LINE }}>
                  <h2 className="m-0 text-[14px] font-bold">Price history</h2>
                  {firstTracked && <span className="text-[12px]" style={{ color: MUTED }}>Since {firstTracked.replace(/, \d{4}$/, "")}</span>}
                </div>
                <div className="relative px-4 pb-4 pt-3.5">
                  <svg width="100%" height="96" viewBox="0 0 280 96" preserveAspectRatio="none" aria-hidden="true" className="opacity-45 blur-[2px]">
                    <path d="M0 0 H280 M0 32 H280 M0 64 H280 M0 95 H280" stroke={LINE} strokeWidth="1" />
                    <path d="M0 28 L44 28 L44 44 L104 44 L104 20 L156 20 L156 58 L208 58 L208 40 L244 40 L244 76 L280 76" fill="none" stroke="var(--hf-deal-link)" strokeWidth="2.5" strokeLinejoin="round" />
                  </svg>
                  <button
                    type="button"
                    onClick={() => gate("history")}
                    className="absolute left-1/2 top-1/2 flex h-9 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-[9px] border bg-[var(--hf-surface)] px-3.5 text-[13px] font-bold shadow-[0_4px_12px_var(--hf-shadow)] hover:bg-[var(--hf-hover)]"
                    style={{ borderColor: CONTROL_BORDER, color: TEXT }}
                  >
                    <LockIcon size={13} />
                    Sign up to view
                  </button>
                </div>
              </section>

              <section className={`${CARD} flex flex-col gap-2 px-[18px] py-4`} style={CARD_BORDER}>
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: HF.amberSoft, color: HF.amberInk }} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
                  </span>
                  <h2 className="m-0 text-[14px] font-bold">In stores near you</h2>
                </div>
                <p className="m-0 text-[13px] leading-[18px]" style={{ color: MUTED }}>
                  Walmart and HD shelf clearance near you is 70%+ off, with aisle and stock for your ZIP.
                </p>
                <Link
                  to={instoreUrl}
                  onClick={() => freeMember ? trackFreeUpgrade("product_page_instore", retailer.slug) : track("guest_signup_started", { surface: "web", retailer: retailer.slug, trigger: "page_instore", source: SOURCE })}
                  className="mt-1 flex h-[38px] items-center justify-center rounded-[10px] text-[13.5px] font-bold no-underline hover:brightness-95"
                  style={{ background: "#FFE9A8", color: "#3D2A00" }}
                >
                  Unlock · {BASIC_INTRO_LABEL} first month
                </Link>
              </section>
            </aside>

              {similar.length > 0 && (
                <section className={`${CARD} min-w-0 overflow-hidden lg:[grid-area:similar]`} style={CARD_BORDER} data-testid="product-page-similar">
                  <div className="flex items-center border-b px-4 py-3 sm:px-5" style={{ borderColor: LINE }}>
                    <h2 className="m-0 flex-1 text-[14.5px] font-bold sm:text-[15px]">Similar deals at {label}</h2>
                    <Link to={categoryId ? `/${retailer.slug}/${encodeURIComponent(categoryId)}` : `/${retailer.slug}`} className="text-[13px] font-semibold no-underline" style={{ color: "var(--hf-deal-link)" }}>
                      View all →
                    </Link>
                  </div>
                  <div className="hidden grid-cols-[minmax(0,1fr)_90px_90px_70px] border-b px-5 py-2 text-[12px] font-semibold sm:grid" style={{ background: HF.hover, borderColor: LINE, color: MUTED }}>
                    <span>Item</span><span className="text-right">Price</span><span className="text-right">List</span><span className="text-right">Off</span>
                  </div>
                  {similar.map((item, index) => {
                    const itemPrice = getGuestDealPrice(item);
                    const itemMsrp = Number(item.msrp) || 0;
                    const itemOff = getGuestDealDiscount(item);
                    return (
                      <button
                        key={`${item.productKey || item.name}-${index}`}
                        type="button"
                        onClick={() => openSimilar(item)}
                        className="flex w-full items-center gap-3 border-b px-4 py-2 text-left transition-colors last:border-b-0 hover:bg-[var(--hf-hover)] sm:grid sm:grid-cols-[minmax(0,1fr)_90px_90px_70px] sm:px-5"
                        style={{ borderColor: HF.divider }}
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-3">
                          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: HF.tile }}>
                            <img src={getProductImageUrl(item.image, "100")} alt="" loading="lazy" className="h-8 w-8 object-contain mix-blend-multiply" />
                          </span>
                          <span className="line-clamp-2 min-w-0 text-[13px] font-medium leading-[17px] sm:line-clamp-1 sm:text-[13.5px]">{cleanName(item.name)}</span>
                        </span>
                        <span className="flex flex-shrink-0 flex-col items-end sm:contents">
                          <span className="text-[14px] font-bold tabular-nums sm:text-right sm:text-[13.5px]">{itemPrice > 0 ? money.format(itemPrice) : "—"}</span>
                          <span className="hidden text-right text-[13.5px] tabular-nums line-through sm:block" style={{ color: HF.muted }}>{itemMsrp > itemPrice ? money.format(itemMsrp) : ""}</span>
                          <span className="text-[12px] font-bold tabular-nums sm:text-right sm:text-[13.5px]" style={{ color: GREEN }}>{itemOff > 0 ? `−${itemOff}%` : ""}</span>
                        </span>
                      </button>
                    );
                  })}
                </section>
              )}
          </div>
        )}
      </main>

      <div className="pb-[76px] sm:pb-0">
        <SiteFooter />
      </div>

      {product && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t bg-[var(--hf-surface)] px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_18px_var(--hf-shadow)] sm:hidden"
          style={{ borderColor: "var(--hf-deal-border)" }}
          data-testid="product-page-sticky-buy"
        >
          <span className="flex flex-col leading-tight">
            <span className="text-[19px] font-extrabold tabular-nums">{price > 0 ? money.format(price) : "See price"}</span>
            {discount > 0 && <span className="text-[12px] font-bold" style={{ color: GREEN }}>−{discount}% off</span>}
          </span>
          <button
            type="button"
            onClick={() => void openRetailer()}
            disabled={opening}
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] text-[14px] font-bold disabled:opacity-70"
            style={{ background: HF.inkBtn, color: HF.onInkBtn }}
          >
            {opening ? "Opening…" : `View on ${label}`}
          </button>
        </div>
      )}
      {auth.modal}
    </div>
  );
}

function Fact({ label, value, color, right = false, last = false }: { label: string; value: string; color?: string; right?: boolean; last?: boolean }) {
  return (
    <div className={`px-3 py-2.5 ${right ? "" : "border-r"} ${last ? "" : "border-b"}`} style={{ borderColor: LINE }}>
      <dt className="text-[12px]" style={{ color: LABEL }}>{label}</dt>
      <dd className="m-0 mt-0.5 font-bold tabular-nums" style={color ? { color } : undefined}>{value}</dd>
    </div>
  );
}

function SecondaryButton({ onClick, icon, children }: { onClick: () => void; icon: ReactNode; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-10 items-center justify-center gap-1.5 rounded-[10px] border bg-[var(--hf-surface)] text-[13.5px] font-semibold transition-colors hover:bg-[var(--hf-hover)]"
      style={{ borderColor: CONTROL_BORDER, color: TEXT }}
    >
      {icon}
      {children}
    </button>
  );
}

function BellIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}

function HeartOutline() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20s-7-4.4-9-8.6C1.6 8.4 3.6 5 7 5c2 0 3.3 1.1 5 3 1.7-1.9 3-3 5-3 3.4 0 5.4 3.4 4 6.4-2 4.2-9 8.6-9 8.6z" />
    </svg>
  );
}
